// A casca: marca, navegação, cabeçalho e a tela ativa.
//
// Sete telas. Seis vêm da PoC ou do domínio novo; a **Visão geral** é nova e
// responde "onde está o gargalo agora?" sem clique — antes era preciso abrir
// card a card para descobrir o que estava travado.
//
// `Ambientes Specifor` e `Projetos`, da PoC, continuam fora: dependem de
// tabelas que o Atlas não portou — o lado de cliente e credencial mora no
// gateway (fase 3).

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import simbolo from "./assets/specifor-symbol.png";
import wordmark from "./assets/specifor-wordmark.png";
import { obter, quemSou, sair, type PainelAgendador, type Sessao } from "./api.js";
import { AvisoFlutuante, type Torrada } from "./pecas/basicos.js";
import { Fronteira } from "./pecas/Fronteira.js";
import { aplicarTema, lembrarTema, temaInicial, type Tema } from "./tema.js";
import { Agendador } from "./telas/Agendador.js";
import { Auditoria } from "./telas/Auditoria.js";
import { Camadas } from "./telas/Camadas.js";
import { Clientes } from "./telas/Clientes.js";
import { Colecoes } from "./telas/Colecoes.js";
import { Entrada } from "./telas/Entrada.js";
import { Fontes } from "./telas/Fontes.js";
import { Mapa } from "./telas/Mapa.js";
import { Operadores } from "./telas/Operadores.js";
import { PainelGeral } from "./telas/Painel.js";

export type Chave =
  | "painel"
  | "camadas"
  | "mapa"
  | "agendador"
  | "fontes"
  | "colecoes"
  | "clientes"
  | "operadores"
  | "auditoria";

const TELAS: { chave: Chave; nome: string; titulo: string; sub: string }[] = [
  {
    chave: "painel",
    nome: "Visão geral",
    titulo: "Visão geral",
    sub: "O funil de curadoria inteiro, e as três razões pelas quais uma publicação trava.",
  },
  {
    chave: "camadas",
    nome: "Camadas",
    titulo: "Camadas",
    sub: "Ingestão por fonte pública, curadoria por exceção e promoção de versão.",
  },
  {
    chave: "mapa",
    nome: "Mapa por tema",
    titulo: "Mapa por tema",
    sub: "O que está publicado, o que foi validado e o que espera julgamento, lado a lado.",
  },
  {
    chave: "agendador",
    nome: "Agendador",
    titulo: "Agendador",
    sub: "O que dispara sozinho, quando — e o que parou de disparar sem avisar.",
  },
  {
    chave: "fontes",
    nome: "Fontes",
    titulo: "Fontes e procedência",
    sub: "Licença, atribuição e restrição de uso. Sem as duas primeiras, não se publica.",
  },
  {
    chave: "colecoes",
    nome: "Coleções",
    titulo: "Coleções publicadas",
    sub: "O que o cliente consome: identificador público, versão vigente e procedência.",
  },
  {
    chave: "clientes",
    nome: "Clientes",
    titulo: "Clientes e credenciais",
    sub: "Quem consome, com qual chave e sobre quais coleções. Chave sem escopo não lê nada.",
  },
  {
    chave: "operadores",
    nome: "Operadores",
    titulo: "Quem opera",
    sub: "Cada pessoa com o próprio usuário — a promoção de versão é uma assinatura.",
  },
  {
    chave: "auditoria",
    nome: "Auditoria",
    titulo: "Auditoria",
    sub: "Append-only. A trava de imutabilidade é do banco, não disciplina no código.",
  },
];

export function App({ temaPadrao }: { temaPadrao?: Tema }): ReactNode {
  const [ativa, setAtiva] = useState<Chave>("painel");
  const [tema, setTema] = useState<Tema>(temaPadrao ?? temaInicial);
  const [aviso, setAviso] = useState<Torrada>(null);
  // Abrir a gaveta a partir da fila da Visão geral: o atalho principal da tela.
  const [abrir, setAbrir] = useState<string | null>(null);

  const cliente = useQueryClient();

  // Aplicar e lembrar são coisas diferentes. Aplicar acontece em toda montagem
  // — inclusive na primeira, em que o valor veio do sistema e ninguém escolheu
  // nada. Lembrar acontece só quando a pessoa clica: gravar o padrão inferido
  // faria a preferência do sistema deixar de ser seguida a partir da primeira
  // visita, o que é o oposto de respeitá-la.
  useEffect(() => aplicarTema(tema), [tema]);
  const escolherTema = (t: Tema): void => {
    setTema(t);
    lembrarTema(t);
  };

  // Quem está operando. Enquanto não se sabe, não se mostra nem o back office
  // nem a tela de entrada: piscar a entrada para quem já tem sessão é pior do
  // que esperar dois quadros.
  const sessao = useQuery<Sessao>({
    queryKey: ["sessao"],
    queryFn: quemSou,
    // Sem repetição: 401 aqui é resposta, não falha de rede. Repetir só atrasa
    // a tela de entrada.
    retry: false,
    refetchOnWindowFocus: true,
  });

  // O DADO, não o status. `isSuccess` pode sobreviver um render ao cache ser
  // limpo — e aí `sessao.data.operador` lança e a árvore inteira desmonta, que
  // foi o que aconteceu ao clicar em "Sair". Perguntar pelo operador em si não
  // tem esse buraco.
  const operador = sessao.data?.operador;

  const avisar = (texto: string, erro = false): void => setAviso({ texto, erro });
  const tela = TELAS.find((t) => t.chave === ativa)!;

  const agendador = useQuery({
    queryKey: ["agendador"],
    queryFn: () => obter<PainelAgendador>("/agendador"),
    refetchInterval: 60_000,
    // Só depois de haver sessão: sem isto, cada tela dispara a própria
    // requisição por trás da entrada e enche o log de 401.
    enabled: operador !== undefined,
  });
  const alertas = agendador.data?.alertas.length ?? 0;

  if (sessao.isPending) return <div className="carregando-casca" aria-busy="true" />;
  if (operador === undefined) {
    return (
      <Entrada
        aoEntrar={() => {
          // Tudo é reconsultado: as telas que levaram 401 antes da entrada
          // guardaram o erro, e sem isto continuariam mostrando-o.
          void cliente.invalidateQueries();
        }}
      />
    );
  }

  const irPara = (c: string): void => {
    setAtiva(c as Chave);
    setAbrir(null);
  };

  return (
    <div className="casca">
      <aside className="lateral">
        <div className="marca">
          <img className="marca-simbolo" src={simbolo} alt="" />
          <img className="marca-wordmark" src={wordmark} alt="Specifor" />
        </div>
        <div className="linha-versao">
          ATLAS
          <hr />
          <b>v1.1</b>
        </div>

        <nav className="nav" aria-label="Telas">
          {TELAS.map((t) => (
            <button
              key={t.chave}
              className="nav-item"
              aria-current={ativa === t.chave ? "page" : undefined}
              onClick={() => irPara(t.chave)}
            >
              <span className="nav-ponto" />
              {t.nome}
              {t.chave === "agendador" && alertas > 0 ? (
                <span className="nav-alerta">{alertas}</span>
              ) : null}
            </button>
          ))}
        </nav>

        <div className="rodape-lateral">
          <div className="legenda-titulo">Ciclo de curadoria</div>
          <ol className="legenda-passos">
            <li>
              <span className="pilula pilula-neutra">ingerido</span>
            </li>
            <li>
              <span className="pilula pilula-aguardando">aguardando validação</span>
            </li>
            <li>
              <span className="pilula pilula-validado">validado</span>
            </li>
            <li>
              <span className="pilula pilula-publicado">publicado</span>
            </li>
          </ol>
          {/* Princípio 7 escrito na tela: quem opera precisa saber que não há
              atalho, e por quê. Texto literal, de propósito. */}
          <p className="nota-principio">
            Não há atalho de ingestão para publicação. Dado público bruto tem geometria inválida e
            duplicata.
          </p>
        </div>
      </aside>

      <main className="principal">
        <header className="topo">
          <div className="topo-texto">
            <h1>{tela.titulo}</h1>
            <p>{tela.sub}</p>
          </div>
          <div className="topo-acoes">
            {/* Fixo: é a decisão Q3 do projeto, não um seletor. */}
            <span className="chip-crs">EPSG:4674</span>
            <div className="troca-tema" role="group" aria-label="Tema">
              <button aria-pressed={tema === "escuro"} onClick={() => escolherTema("escuro")}>
                Escuro
              </button>
              <button aria-pressed={tema === "claro"} onClick={() => escolherTema("claro")}>
                Claro
              </button>
            </div>
            {/* A identidade vem da SESSÃO. Era literal no JSX — um avatar "CU"
                e a palavra "Curadoria" — e aparecia igual numa tela que não
                havia conseguido autenticar: identidade falsa é pior que
                nenhuma, ainda mais onde a promoção é uma assinatura. */}
            <div className="curador">
              <span className="avatar" aria-hidden="true">
                {operador.slice(0, 2).toUpperCase()}
              </span>
              <span>
                <span className="curador-nome" style={{ display: "block" }}>
                  {operador}
                </span>
                {/* Entrar pela credencial de EMERGÊNCIA é exceção (ADR-013) —
                    passar por normal é como uma senha de infraestrutura vira a
                    senha de todo dia. */}
                {sessao.data?.origem === "emergencia" ? (
                  <span className="pilula pilula-aguardando">emergência</span>
                ) : null}
                <button
                  className="curador-sair"
                  type="button"
                  onClick={() => {
                    // `clear`, e não `invalidate`: o que foi carregado sob a
                    // sessão anterior — camadas, fontes, auditoria — não pode
                    // sobrar em memória para a próxima pessoa que entrar nesta
                    // aba. Invalidar só marcaria como velho; os dados ficariam.
                    void sair().then(() => {
                      cliente.clear();
                      // `clear` esvazia o cache mas NÃO refaz nada sozinho — a
                      // casca ficaria em branco esperando uma consulta que
                      // ninguém disparou. O `refetch` é o que devolve a tela
                      // de entrada.
                      void sessao.refetch();
                    });
                  }}
                >
                  Sair
                </button>
              </span>
            </div>
          </div>
        </header>

        {/* A fronteira fica em volta do CONTEÚDO, não da casca: uma tela que
            quebra custa aquela tela, e a navegação e o "Sair" continuam de pé.
            Já aconteceu o contrário em produção — página em branco inteira. */}
        <Fronteira chave={ativa}>
          <div className="conteudo">
            {ativa === "painel" ? (
              <PainelGeral
                aoIrPara={irPara}
                aoAbrirCamada={(id) => {
                  setAtiva("camadas");
                  setAbrir(id);
                }}
              />
            ) : null}
            {ativa === "camadas" ? (
              <Camadas avisar={avisar} abrir={abrir} aoFechar={() => setAbrir(null)} />
            ) : null}
            {ativa === "mapa" ? <Mapa aparencia={tema} /> : null}
            {ativa === "agendador" ? <Agendador avisar={avisar} /> : null}
            {ativa === "fontes" ? <Fontes avisar={avisar} /> : null}
            {ativa === "colecoes" ? <Colecoes avisar={avisar} /> : null}
            {ativa === "clientes" ? <Clientes avisar={avisar} /> : null}
            {ativa === "operadores" ? <Operadores avisar={avisar} atual={operador} /> : null}
            {ativa === "auditoria" ? <Auditoria /> : null}
          </div>
        </Fronteira>
      </main>

      <AvisoFlutuante aviso={aviso} aoSumir={() => setAviso(null)} />
    </div>
  );
}
